// Fitted trust-overlay surfaces (3D).
//
// The 3D analogue of Tailor's slice strip: each source model is fitted over
// its varying axes and drawn as its own translucent surface over the
// (x, y) grid, where SURFACE OPACITY TRACKS CONFIDENCE — a model fades to
// ghostly exactly where it is extrapolating or in conflict. An optional
// fused surface (confidence-weighted blend, /composite/surface) overlays the
// lot. Held axes + x/y come from the explorer's Tailor controls, so moving
// them re-slices the surfaces.
//
// three.js is dynamically imported (keeps the module light and out of the
// jsdom test path). Per-vertex alpha is a small onBeforeCompile patch on
// MeshStandardMaterial (preserves PBR lighting) — see ALPHA notes.
//
// FXC-9009 adversarial-review fixes:
//   - the renderer/scene/camera PERSIST across data changes (they used to be
//     torn down + rebuilt per fetch — a WebGL context churn per slice move);
//     only the surface content (meshes/point clouds) rebuilds;
//   - render-on-demand: a render is kicked by interaction, resize and data
//     swaps instead of an unconditional per-instance rAF loop (N idle 3D
//     lanes used to burn N free-running render loops);
//   - `surface` injection seam: a parent (StudyTrustSurface) may supply the
//     already-fetched response so N lanes share ONE canonical query; the
//     internal query only runs when nothing is injected (legacy callers);
//   - controlled `view`/`onViewChange` (the shared Study-Cursor 3D camera,
//     MannequinSurfaceViewState shape) and controlled `fused`/`onFusedChange`
//     (the shared evidence lens) — both optional; legacy callers keep the
//     internal state.
//
// FXC-9009 geometry-breadth additions:
//   - optional `axisOptions` + change handlers render x/y axis dropdowns in
//     the header so the user can re-project the surface onto ANY canonical
//     composite axis (e.g. a per-element geometry shape DOF);
//   - the canvas container is ALWAYS mounted: the persistent scene controller
//     is attached to it once, so unmounting it while a re-projection loads
//     would orphan the renderer on a detached element (a dead panel). Status
//     (loading / error / empty / degenerate axes) is a pointer-events:none
//     overlay ON TOP of the canvas instead;
//   - `refreshToken` folds into the internal query key to force a refetch
//     (e.g. after a solve ingests new runs) WITHOUT remounting the panel.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchSurface, type SurfaceResponse } from "../../api/composite";
import { Icons } from "../thread-v4/icons";
import { SOURCE_CLASS_LABEL } from "./composite/palette";
import type { SourceClass } from "../../api/composite";

const SOURCE_VAR: Record<string, string> = {
  rom: "--v5-ink-4",
  cfd: "--v5-blue",
  wind_tunnel: "--v5-amber",
  flight_test: "--v5-primary",
  manual: "--v5-warn",
  gp: "--v5-purple",
};
const SOURCE_FALLBACK: Record<string, string> = {
  rom: "#8a8f98", cfd: "#4a90d9", wind_tunnel: "#c8962e", flight_test: "#2e9e5b", manual: "#c2603a", gp: "#7f77dd",
};
const LAYER_LABEL: Record<string, string> = {
  rom: "AVL", cfd: "CFD/RANS", wind_tunnel: "WT", flight_test: "FT", manual: "Manual", gp: "GP",
};

function cssColor(sourceClass: string): string {
  const fallback = SOURCE_FALLBACK[sourceClass] ?? "#8a8f98";
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(SOURCE_VAR[sourceClass] ?? "").trim();
  return v || fallback;
}

const LAYOUT = { width: 2.0, depth: 2.0, height: 1.0 };
/** Default camera pose (matches the pre-refactor constants). */
const DEFAULT_VIEW: TrustView = { yaw: -0.6, pitch: 0, zoom: 1, panX: 0, panY: 0 };
const BASE_CAMERA = { x: 2.6, y: 2.2, z: 2.6 } as const;
const VIEW_EPS = 1e-3;

export type TrustView = { yaw: number; pitch: number; zoom: number; panX: number; panY: number };

// Loading/empty/error are drawn as an overlay ON TOP of the (always-mounted)
// canvas container — see the render note below for why the canvas can never be
// conditionally unmounted. pointer-events:none so it never steals orbit drags.
const STATUS_OVERLAY: CSSProperties = {
  position: "absolute", inset: 0, minHeight: 0, zIndex: 1, display: "flex",
  alignItems: "center", justifyContent: "center", gap: 6, pointerEvents: "none",
  textAlign: "center",
};

/** One selectable response-surface axis (value = canonical composite axis name). */
export interface SurfaceAxisOption {
  value: string;
  label: string;
  /** Optional grouping for the dropdown's <optgroup> (e.g. "Geometry"). */
  group?: string;
}

interface Props {
  projectId: string;
  branch: string;
  /** Composite policy. Optional: when omitted, fetchSurface/baseParams omit the
   *  param and the server applies its default policy (matches the High-Lift
   *  composite tiles, which have no policy in scope). */
  policy?: string;
  quantity: string;
  axisX: string;
  axisY: string;
  fixed: Record<string, number>;
  /** Grow the 3D canvas to fill its container height (right-rail layout). */
  fill?: boolean;
  /** Injected surface data — when the parent owns fetching (the shared
   *  study-surface query), the internal query is disabled entirely. */
  surface?: SurfaceResponse;
  surfaceLoading?: boolean;
  surfaceError?: Error | null;
  /** Controlled camera (shared Study-Cursor view3d). Optional. */
  view?: TrustView;
  onViewChange?: (view: TrustView) => void;
  /** Controlled fused/per-model mode (shared evidence lens). Optional. */
  fused?: boolean;
  onFusedChange?: (fused: boolean) => void;
  /** When supplied (with the change handlers), the header renders x/y axis
   *  dropdowns so the user can re-project the surface onto any composite axis
   *  (e.g. a geometry shape DOF). Options are typically sourced from the design
   *  knob registry; their `value` must be a canonical composite axis name
   *  (the backend 400s an unknown axis). Omit to keep the static label. */
  axisOptions?: SurfaceAxisOption[];
  onAxisXChange?: (axis: string) => void;
  onAxisYChange?: (axis: string) => void;
  /** Bump to force a surface REFETCH (e.g. after a solve ingests new runs) WITHOUT
   *  remounting the panel — folded into the react-query key. Replaces the old
   *  `key={nonce}` remount that churned the WebGL context. */
  refreshToken?: number;
}

/** Header axis dropdown: lists options grouped by their `group`, excluding the
 *  axis currently on the OTHER dropdown (x !== y is enforced by the query). */
function AxisSelect({
  value, options, exclude, onChange, testid,
}: {
  value: string;
  options: SurfaceAxisOption[];
  exclude: string;
  onChange: (axis: string) => void;
  testid: string;
}) {
  const groups = useMemo(() => {
    const by = new Map<string, SurfaceAxisOption[]>();
    for (const o of options) {
      const g = o.group ?? "Axis";
      (by.get(g) ?? by.set(g, []).get(g)!).push(o);
    }
    return Array.from(by.entries());
  }, [options]);
  return (
    <select
      className="v5-adbc-axis-select"
      value={value}
      data-testid={testid}
      onChange={(e) => onChange(e.target.value)}
      style={{ font: "inherit", color: "inherit", background: "transparent", border: "1px solid var(--v5-line, #3334)", borderRadius: 5, padding: "1px 4px", cursor: "pointer" }}
    >
      {groups.map(([g, opts]) => (
        <optgroup key={g} label={g}>
          {opts.map((o) => (
            <option key={o.value} value={o.value} disabled={o.value === exclude}>{o.label}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

// ── imperative three.js controller (persists across data changes) ───────────

type SceneCtrl = {
  setContent: (data: SurfaceResponse, showFused: boolean, hidden: Set<string>) => void;
  applyView: (view: TrustView) => void;
  dispose: () => void;
};

async function createSceneCtrl(
  el: HTMLDivElement,
  opts: { onViewCommit?: (view: TrustView) => void },
): Promise<SceneCtrl> {
  const THREE = await import("three");
  const width = el.clientWidth || 480;
  const height = el.clientHeight || 320;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 100);
  camera.position.set(BASE_CAMERA.x, BASE_CAMERA.y, BASE_CAMERA.z);
  camera.lookAt(0, 0, 0);
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(width, height);
  el.appendChild(renderer.domElement);

  scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 1.05));
  const dir = new THREE.DirectionalLight(0xffffff, 0.7);
  dir.position.set(3, 6, 2);
  scene.add(dir);

  const group = new THREE.Group();
  group.rotation.y = DEFAULT_VIEW.yaw;
  group.rotation.x = DEFAULT_VIEW.pitch;
  scene.add(group);

  // ── render on demand: interactions/data/resize kick ONE coalesced frame ──
  let raf = 0;
  let disposed = false;
  const invalidate = () => {
    if (disposed || raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      renderer.render(scene, camera);
    });
  };

  const baseLen = Math.hypot(BASE_CAMERA.x, BASE_CAMERA.y, BASE_CAMERA.z);
  const currentView = (): TrustView => ({
    yaw: group.rotation.y,
    pitch: group.rotation.x,
    zoom: baseLen / (camera.position.length() || baseLen),
    panX: 0,
    panY: 0,
  });
  let commitTimer: ReturnType<typeof setTimeout> | null = null;
  const commitView = () => {
    if (!opts.onViewCommit) return;
    if (commitTimer) clearTimeout(commitTimer);
    // trailing-debounced so a wheel burst / drag settles into one store write
    commitTimer = setTimeout(() => {
      commitTimer = null;
      opts.onViewCommit?.(currentView());
    }, 200);
  };

  // minimal orbit: drag to rotate, wheel to dolly — each move renders ONE frame
  let dragging = false; let lx = 0; let ly = 0;
  const onDown = (e: PointerEvent) => { dragging = true; lx = e.clientX; ly = e.clientY; };
  const onUp = () => {
    if (dragging) commitView();
    dragging = false;
  };
  const onMove = (e: PointerEvent) => {
    if (!dragging) return;
    group.rotation.y += (e.clientX - lx) * 0.01;
    group.rotation.x = Math.max(-1.2, Math.min(1.2, group.rotation.x + (e.clientY - ly) * 0.008));
    lx = e.clientX; ly = e.clientY;
    invalidate();
  };
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    camera.position.multiplyScalar(e.deltaY > 0 ? 1.08 : 0.93);
    invalidate();
    commitView();
  };
  renderer.domElement.addEventListener("pointerdown", onDown);
  window.addEventListener("pointerup", onUp);
  window.addEventListener("pointermove", onMove);
  renderer.domElement.addEventListener("wheel", onWheel, { passive: false });

  const ro = new ResizeObserver(() => {
    const w = el.clientWidth || 480; const h = el.clientHeight || 320;
    renderer.setSize(w, h); camera.aspect = w / h; camera.updateProjectionMatrix();
    invalidate();
  });
  ro.observe(el);

  // ── swappable content (meshes + point clouds + plate) ──────────────────────
  let disposables: Array<{ dispose(): void }> = [];
  const clearContent = () => {
    for (const d of disposables) d.dispose();
    disposables = [];
    group.clear();
  };

  const setContent = (data: SurfaceResponse, showFused: boolean, hidden: Set<string>) => {
    if (disposed) return;
    clearContent();

    // shared vertical scale across all surfaces, so overlays are comparable
    const allZ: number[] = [];
    for (const m of data.models) for (const z of m.z) if (z != null) allZ.push(z);
    for (const z of data.fused.z) if (z != null) allZ.push(z);
    const zMin = allZ.length ? Math.min(...allZ) : 0;
    const zMax = allZ.length ? Math.max(...allZ) : 1;
    const zSpan = zMax - zMin || 1;

    const { nx, ny } = data;
    const px = (i: number) => -LAYOUT.width / 2 + (i / (nx - 1 || 1)) * LAYOUT.width;
    const pz = (j: number) => -LAYOUT.depth / 2 + (j / (ny - 1 || 1)) * LAYOUT.depth;
    const py = (v: number) => ((v - zMin) / zSpan - 0.5) * LAYOUT.height;

    const meshes: Array<{ mesh: InstanceType<typeof THREE.Mesh>; meanY: number }> = [];

    const buildMesh = (
      z: (number | null)[], confidence: number[], colorHex: string, opacityFloor: number,
    ) => {
      const positions: number[] = [];
      const confAttr: number[] = [];
      const vIndex = new Int32Array(nx * ny).fill(-1);
      let vCount = 0;
      let sumY = 0;
      for (let j = 0; j < ny; j += 1) {
        for (let i = 0; i < nx; i += 1) {
          const idx = j * nx + i;
          const v = z[idx];
          if (v == null) continue;
          vIndex[idx] = vCount;
          const y = py(v);
          positions.push(px(i), y, pz(j));
          confAttr.push(Math.max(opacityFloor, confidence[idx] ?? 0));
          sumY += y;
          vCount += 1;
        }
      }
      if (vCount < 3) return null;
      const indices: number[] = [];
      for (let j = 0; j < ny - 1; j += 1) {
        for (let i = 0; i < nx - 1; i += 1) {
          const a = vIndex[j * nx + i] ?? -1;
          const b = vIndex[j * nx + i + 1] ?? -1;
          const c = vIndex[(j + 1) * nx + i] ?? -1;
          const d = vIndex[(j + 1) * nx + i + 1] ?? -1;
          if (a < 0 || b < 0 || c < 0 || d < 0) continue; // only full quads
          indices.push(a, c, b, b, c, d);
        }
      }
      if (!indices.length) return null;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
      geo.setAttribute("confidence", new THREE.Float32BufferAttribute(confAttr, 1));
      geo.setIndex(indices);
      geo.computeVertexNormals();
      const mat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(colorHex), side: THREE.DoubleSide,
        roughness: 0.7, metalness: 0.02, transparent: true, depthWrite: false,
      });
      // per-vertex confidence → fragment alpha (preserve PBR lighting)
      mat.onBeforeCompile = (shader) => {
        shader.vertexShader = shader.vertexShader
          .replace("void main() {", "attribute float confidence;\nvarying float vConf;\nvoid main() {")
          .replace("#include <begin_vertex>", "#include <begin_vertex>\n  vConf = confidence;");
        shader.fragmentShader = shader.fragmentShader
          .replace("void main() {", "varying float vConf;\nvoid main() {")
          .replace("#include <dithering_fragment>", "#include <dithering_fragment>\n  gl_FragColor.a *= clamp(0.12 + 0.88 * vConf, 0.0, 1.0);");
      };
      const mesh = new THREE.Mesh(geo, mat);
      disposables.push(geo, mat);
      return { mesh, meanY: sumY / vCount };
    };

    if (showFused) {
      const fused = buildMesh(data.fused.z, data.fused.confidence, cssColor("flight_test"), 0.1);
      if (fused) meshes.push(fused);
    } else {
      for (const model of data.models) {
        if (hidden.has(model.layer_id)) continue;
        const built = buildMesh(model.z, model.confidence, cssColor(model.source_class), 0.12);
        if (built) meshes.push(built);
      }
    }
    // back-to-front by mean height to reduce transparency z-fighting
    meshes.sort((a, b) => a.meanY - b.meanY);
    for (const m of meshes) group.add(m.mesh);

    // base plate for spatial reference
    const plateGeo = new THREE.PlaneGeometry(LAYOUT.width * 1.04, LAYOUT.depth * 1.04);
    const plateMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(cssColor("rom")), transparent: true, opacity: 0.06, side: THREE.DoubleSide });
    const plate = new THREE.Mesh(plateGeo, plateMat);
    plate.rotation.x = -Math.PI / 2;
    plate.position.y = -LAYOUT.height / 2 - 0.02;
    group.add(plate);
    disposables.push(plateGeo, plateMat);

    // Raw data points — the ACTUAL runs (RANS/AVL), so you can see each
    // surface pass through (or depart from) the measured/computed evidence.
    // Mapped from data coordinates via the surface's own axis/value bounds.
    const [xLo, xHi] = data.x_bounds;
    const [yLo, yHi] = data.y_bounds;
    const sx = (x: number) => -LAYOUT.width / 2 + ((x - xLo) / ((xHi - xLo) || 1)) * LAYOUT.width;
    const sz = (y: number) => -LAYOUT.depth / 2 + ((y - yLo) / ((yHi - yLo) || 1)) * LAYOUT.depth;
    for (const s of data.samples ?? []) {
      if (!showFused && hidden.has(s.source_class)) continue;
      const pos: number[] = [];
      for (const p of s.points) pos.push(sx(p.x), py(p.value), sz(p.y));
      if (!pos.length) continue;
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
      const mat = new THREE.PointsMaterial({
        color: new THREE.Color(cssColor(s.source_class)), size: 0.045,
        sizeAttenuation: true, transparent: true, opacity: 0.95, depthWrite: false,
      });
      const cloud = new THREE.Points(geo, mat);
      group.add(cloud);
      disposables.push(geo, mat);
    }

    invalidate();
  };

  const applyView = (view: TrustView) => {
    if (disposed) return;
    const cur = currentView();
    if (
      Math.abs(cur.yaw - view.yaw) < VIEW_EPS &&
      Math.abs(cur.pitch - view.pitch) < VIEW_EPS &&
      Math.abs(cur.zoom - view.zoom) < VIEW_EPS
    ) {
      return; // our own commit echoing back — no-op, no render
    }
    group.rotation.y = view.yaw;
    group.rotation.x = Math.max(-1.2, Math.min(1.2, view.pitch));
    const zoom = Number.isFinite(view.zoom) && view.zoom > 0.05 ? view.zoom : 1;
    camera.position.setLength(baseLen / zoom);
    invalidate();
  };

  const dispose = () => {
    disposed = true;
    if (raf) cancelAnimationFrame(raf);
    if (commitTimer) clearTimeout(commitTimer);
    ro.disconnect();
    renderer.domElement.removeEventListener("pointerdown", onDown);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointermove", onMove);
    renderer.domElement.removeEventListener("wheel", onWheel);
    clearContent();
    renderer.dispose();
    if (renderer.domElement.parentNode === el) el.removeChild(renderer.domElement);
  };

  return { setContent, applyView, dispose };
}

export function TrustSurfacePanel({
  projectId, branch, policy, quantity, axisX, axisY, fixed, fill,
  surface: surfaceProp, surfaceLoading, surfaceError,
  view, onViewChange, fused, onFusedChange,
  axisOptions, onAxisXChange, onAxisYChange, refreshToken,
}: Props) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [showFusedLocal, setShowFusedLocal] = useState(false);
  const showFused = fused ?? showFusedLocal;
  const setShowFused = (v: boolean) => (onFusedChange ? onFusedChange(v) : setShowFusedLocal(v));
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const injected = surfaceProp !== undefined || surfaceLoading !== undefined || surfaceError != null;
  const axesValid = axisX !== axisY;
  // surfaces fetched at sharpness=1 (inverse-variance) for the quick-win;
  // the ζ/ξ + per-model belief sliders are the Phase-3 fast-follow.
  // Internal query only for LEGACY callers — injected mode shares the parent's
  // canonical study-surface query instead (one fetch per slice for all lanes).
  const query = useQuery({
    queryKey: ["composite-surface", projectId, branch, policy, quantity, axisX, axisY, JSON.stringify(fixed), refreshToken],
    queryFn: ({ signal }) => fetchSurface({ projectId, branch, policy, quantity, axisX, axisY, nx: 32, ny: 20, fixed, signal }),
    enabled: !injected && !!projectId && axesValid,
  });
  const data = axesValid ? (injected ? surfaceProp : query.data) : undefined;
  const isLoading = injected ? !!surfaceLoading && !data : query.isLoading;
  const errorMessage = injected
    ? surfaceError?.message ?? null
    : query.isError ? (query.error as Error).message : null;

  const toggle = (id: string) => setHidden((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  // ── persistent scene controller: create once per canvas mount, swap only
  //    the content on data/mode changes, render on demand ────────────────────
  const ctrlRef = useRef<SceneCtrl | null>(null);
  const ctrlPromiseRef = useRef<Promise<SceneCtrl | null> | null>(null);
  const onViewChangeRef = useRef(onViewChange);
  onViewChangeRef.current = onViewChange;
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !data) return;
    let cancelled = false;
    const ready = (ctrl: SceneCtrl | null) => {
      if (!ctrl || cancelled) return;
      ctrl.setContent(data, showFused, hidden);
      if (viewRef.current) ctrl.applyView(viewRef.current);
    };
    if (ctrlRef.current) {
      ready(ctrlRef.current);
      return;
    }
    if (!ctrlPromiseRef.current) {
      ctrlPromiseRef.current = createSceneCtrl(mount, {
        onViewCommit: (v) => onViewChangeRef.current?.(v),
      })
        .then((ctrl) => {
          ctrlRef.current = ctrl;
          return ctrl;
        })
        .catch((err: unknown) => {
          // WebGL/headless failures must not crash the surface — log and bail.
          // eslint-disable-next-line no-console
          console.warn("TrustSurfacePanel: 3D render unavailable", err);
          ctrlPromiseRef.current = null;
          return null;
        });
    }
    void ctrlPromiseRef.current.then(ready);
    return () => { cancelled = true; };
  }, [data, showFused, hidden]);

  // Follow the SHARED camera: another lane's orbit applies here (a change that
  // is our own commit echoing back is epsilon-guarded inside applyView).
  useEffect(() => {
    if (view && ctrlRef.current) ctrlRef.current.applyView(view);
  }, [view]);

  // Dispose the renderer/scene ONCE, on unmount (not per data change).
  useEffect(() => () => {
    ctrlRef.current?.dispose();
    ctrlRef.current = null;
    ctrlPromiseRef.current = null;
  }, []);

  const legend = useMemo(() => (data?.models ?? []).map((m) => ({
    id: m.layer_id, label: LAYER_LABEL[m.source_class] ?? SOURCE_CLASS_LABEL[m.source_class as SourceClass] ?? m.source_class, color: SOURCE_FALLBACK[m.source_class] ?? "#8a8f98",
  })), [data]);

  return (
    <div data-testid="trust-surface-panel" className={fill ? "tl-fill" : undefined}>
      <div className="v5-adb-tailor-strip" style={{ marginBottom: 8, alignItems: "center" }}>
        {axisOptions && axisOptions.length && (onAxisXChange || onAxisYChange) ? (
          <span className="v5-adbc-panel-note" style={{ display: "inline-flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
            <AxisSelect value={axisX} options={axisOptions} exclude={axisY} onChange={onAxisXChange ?? (() => {})} testid="surface-axis-x" />
            <span aria-hidden>×</span>
            <AxisSelect value={axisY} options={axisOptions} exclude={axisX} onChange={onAxisYChange ?? (() => {})} testid="surface-axis-y" />
            <span>· opacity = confidence</span>
          </span>
        ) : (
          <span className="v5-adbc-panel-note">{axisX} × {axisY} · surface opacity = confidence</span>
        )}
        <div className="v5-adb-evidence-tabs" role="group" aria-label="Surface mode">
          <button type="button" className={showFused ? "" : "is-active"} onClick={() => setShowFused(false)} data-testid="surface-mode-models">Per model</button>
          <button type="button" className={showFused ? "is-active" : ""} onClick={() => setShowFused(true)} data-testid="surface-mode-fused">Fused</button>
        </div>
        {!showFused ? (
          <div className="v5-adbc-legend" style={{ padding: 0 }}>
            {legend.map((l) => (
              <button key={l.id} type="button" className="v5-adbc-legend-item" style={{ opacity: hidden.has(l.id) ? 0.4 : 1, background: "none", border: "none", cursor: "pointer", color: "inherit" }} onClick={() => toggle(l.id)} title="Toggle this model surface">
                <i className="v5-adbc-dot" style={{ ["--dot" as string]: l.color }} />{l.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {/* The canvas container is ALWAYS mounted (even for the degenerate
          same-axis case): the persistent scene controller attaches to this div
          once, so unmounting it while a re-projection loads (or while axes
          match) would strand the renderer on a detached element — a dead,
          non-interactive panel. Status is a pointer-events:none overlay. */}
      <div ref={mountRef} className="v5-adbc-surface-canvas" data-testid="surface-canvas"
           style={fill ? { flex: 1, minHeight: 360, height: "auto", position: "relative" } : { position: "relative" }}>
        {!axesValid ? (
          <div className="v5-adbc-chart-empty" style={STATUS_OVERLAY} data-testid="surface-empty">Pick two different axes to fit a surface.</div>
        ) : isLoading ? (
          <div className="v5-adbc-chart-empty" style={STATUS_OVERLAY}><Icons.Refresh size={16} />Fitting surfaces…</div>
        ) : errorMessage ? (
          <div className="v5-adbc-chart-empty" style={STATUS_OVERLAY} data-testid="surface-error">{errorMessage}</div>
        ) : data && data.models.length === 0 ? (
          <div className="v5-adbc-chart-empty" style={STATUS_OVERLAY} data-testid="surface-empty">No model fit a surface over {axisX} × {axisY} at this slice.</div>
        ) : null}
      </div>
      <div className="v5-adbc-panel-note">Drag to rotate · wheel to zoom · ghostly = extrapolating or in conflict</div>
    </div>
  );
}
